import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ChangeLog, Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import {
  Blacklist,
  BlacklistData,
  BlacklistSettings,
  BlacklistValidators,
  RecordItem,
} from '../../types';

export interface BlacklistValidatorsResponse {
  data: BlacklistValidators;
}

export interface BlacklistBody {
  name: string;
  description?: string;
  source?: string;
}

export const blacklistApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBlacklistValidators: build.query<BlacklistValidatorsResponse, void>({
      query: () => ({
        url: '/v1/blacklists/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBlacklists: build.query<Response<Blacklist>, string>({
      query: (query) => ({
        url: `/v1/blacklists${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBlacklistsItems: build.query<Response<Blacklist>, void>({
      query: () => ({
        url: `/v1/blacklists?all=true&sortDir=desc&sortProp=createdAt`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createBlacklist: build.mutation<{ data: Blacklist }, BlacklistBody>({
      query: (body: BlacklistBody) => ({
        url: '/v1/blacklists',
        method: 'POST',
        body: {
          name: body.name,
          description: body?.description,
          source: body?.source,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateBlacklistSettings: build.mutation<
      { data: BlacklistSettings & { id: string; blacklistId: string } },
      any
    >({
      query: (data: { body: BlacklistSettings; id: string }) => ({
        url: `/v1/blacklists/${data.id}/settings`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBlacklistTemplate: build.query<Response<string>, void>({
      query: () => ({
        url: `/v1/blacklists/template`,
        responseHandler: (response) => response.text(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBlacklist: build.query<
      { data: Blacklist },
      { id: string; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/blacklists/${id}?${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBlacklistDataItem: build.query<
      { data: BlacklistData },
      { id: string; dataId: string }
    >({
      query: (data) => ({
        url: `/v1/blacklists/${data.id}/data/${data.dataId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBlacklistData: build.query<
      Response<BlacklistData>,
      { id: string; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/blacklists/${id}/data${query?.replace('cursor', 'skip')}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateBlacklist: build.mutation<
      { data: Blacklist },
      { body: BlacklistBody; id: string }
    >({
      query: (data) => ({
        url: `/v1/blacklists/${data.id}`,
        method: 'PUT',
        body: {
          name: data.body.name,
          description: data.body?.description,
          source: data.body?.source,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    uploadBlacklistData: build.mutation({
      async queryFn(
        body: { id: string; file: File },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const formData = new FormData();

        formData.append('file', body.file);
        formData.append('title', body.file.name);
        formData.append('type', body.file.type);

        const response = await fetchWithBQ({
          url: `/v1/blacklists/${body.id}/data`,
          method: 'POST',
          body: formData,
        });

        return response
          ? { data: response }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    toggleBlacklistStatus: build.mutation<
      { data: Blacklist },
      { active: boolean; id: string }
    >({
      query: (data) => ({
        url: `/v1/blacklists/${data.id}/status`,
        method: 'PUT',
        body: { active: data.active },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBlacklistsData: build.query<Response<BlacklistData>, string>({
      query: (query) => ({
        url: `/v1/blacklists/data-search${query?.replace('cursor', 'skip')}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBlacklistsChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addBlacklistItem: build.mutation<
      { data: BlacklistData },
      { body: RecordItem; id: string }
    >({
      query: (data) => ({
        url: `/v1/blacklists/${data.id}/items`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteBlacklistItem: build.mutation<
      { data: BlacklistData },
      { itemId: string; id: string }
    >({
      query: (data) => ({
        url: `/v1/blacklists/${data.id}/items/${data.itemId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetBlacklistsQuery,
  useLazyGetBlacklistsQuery,
  useLazyGetBlacklistValidatorsQuery,
  useCreateBlacklistMutation,
  useUpdateBlacklistSettingsMutation,
  useGetBlacklistTemplateQuery,
  useGetBlacklistQuery,
  useLazyGetBlacklistQuery,
  useUpdateBlacklistMutation,
  useToggleBlacklistStatusMutation,
  useLazyGetBlacklistsDataQuery,
  useLazyGetBlacklistDataQuery,
  useUploadBlacklistDataMutation,
  useLazyGetBlacklistDataItemQuery,
  useLazyGetBlacklistsItemsQuery,
  useLazyGetBlacklistsChangeLogsQuery,
  useAddBlacklistItemMutation,
  useDeleteBlacklistItemMutation,
} = blacklistApi;

export const {
  endpoints: {
    getBlacklists,
    getBlacklistValidators,
    createBlacklist,
    updateBlacklistSettings,
    getBlacklistTemplate,
    getBlacklist,
    updateBlacklist,
    toggleBlacklistStatus,
    getBlacklistsData,
    getBlacklistData,
    uploadBlacklistData,
    getBlacklistDataItem,
    getBlacklistsItems,
    getBlacklistsChangeLogs,
    addBlacklistItem,
    deleteBlacklistItem,
  },
} = blacklistApi;
